/* QA-011 — decisión §16 dentro del flujo rápido: el viático base no se rinde; puede cubrir la diferencia de esta boleta.
 * La relación gasto↔unidades la decide el usuario (revisable por Coordinador/Finanzas); nada se aplica sin su elección. */
import { useMemo, useState } from 'react';
import { Alert, Button } from '@/shared/ui';
import { formatClp } from '@/shared/lib/format';
import { ApiError, errorMessage, put } from '@/shared/api/client';
import { subtypeRules } from '@/shared/ui/SubtypeSelect';
import type { Expense, Unit } from '@/features/reports/api';
import { expenseToInput } from './CompleteStep';

/** Aplica (o quita) unidades de viático a un gasto guardado; `selection` queda auditada en el backend (AUTO/MANUAL/NONE). */
export async function applyPerDiem(e: Expense, unitIds: number[], selection: 'AUTO' | 'MANUAL' | 'NONE', confirm = false): Promise<Expense> {
  return put<Expense>(`/reports/${e.expense_report_id}/expenses/${e.id}`, { ...expenseToInput(e), per_diem_unit_ids: unitIds, consume_without_difference: confirm, per_diem_selection: selection });
}
import s from './NewExpenseFlow.module.css';

/** Unidades disponibles que calzan con el gasto: por subtipo (per_diem_concept de la opción, QA-006) o por los conceptos de la categoría. */
export function matchingUnits(e: Expense, units: Unit[] | undefined, opts: { includeConsumed?: boolean } = {}): Unit[] {
  const rules = e.category.rules as { allows_per_diem_offset?: boolean; per_diem_concepts?: string[] };
  if (!rules.allows_per_diem_offset || !units?.length) return [];
  const own = opts.includeConsumed ? [...(units ?? []), ...e.per_diem_offsets.map((o) => ({ id: o.per_diem_unit_id, owner_user_id: o.owner_user_id, owner_name: o.owner_name, fund_id: 0, concept: o.concept, zone: o.zone, unit_amount_clp: o.unit_amount_clp, activity_date: null }))] : units ?? [];
  units = own as Unit[];
  const st = subtypeRules(e.category.rules);
  const opt = st?.options.find((o) => o.code === e.subtype_code) as { per_diem_concept?: string } | undefined;
  const concepts = opt?.per_diem_concept ? [opt.per_diem_concept] : rules.per_diem_concepts ?? [];
  return units.filter((u) => concepts.includes(u.concept));
}

export function PerDiemStep({ expense, units, onDone }: { expense: Expense; units: Unit[]; onDone: (e: Expense) => void }) {
  const [sel, setSel] = useState<number[]>(units.length === 1 ? [units[0].id] : expense.per_diem_offsets.map((o) => o.per_diem_unit_id));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const total = expense.document_total_clp ?? 0;
  const covered = useMemo(() => Math.min(total, units.filter((u) => sel.includes(u.id)).reduce((a, u) => a + u.unit_amount_clp, 0)), [sel, units, total]);
  const apply = async (unitIds: number[], confirm = false) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const e = await applyPerDiem(expense, unitIds, unitIds.length ? 'MANUAL' : 'NONE', confirm);
      onDone(e);
    } catch (ex) {
      if (ex instanceof ApiError && ex.code === 'per_diem_confirm_required') { if (window.confirm(`La boleta (${formatClp(total)}) no supera el viático seleccionado: se consumirá sin diferencia imputable. ¿Continuar?`)) { setBusy(false); await apply(unitIds, true); return; } }
      else setErr(errorMessage(ex));
    } finally { setBusy(false); }
  };
  return (
    <div className={s.form} data-testid="perdiem-step">
      <p className={s.lead}>Recibiste viático para esto. Si pagaste esta boleta ({formatClp(total)}) en una comida cubierta por tu viático, el viático cubre primero y solo la diferencia se descuenta del fondo.</p>
      <div role="group" aria-label="Viáticos disponibles" className={s.reportList}>
        {units.map((u) => (
          <label key={u.id} className={`${s.reportBtn} ${sel.includes(u.id) ? s.reportSel : ''}`} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" checked={sel.includes(u.id)} onChange={(ev) => setSel(ev.target.checked ? [...sel, u.id] : sel.filter((x) => x !== u.id))} />
            <span><span className={s.reportTitle}>{u.concept.charAt(0) + u.concept.slice(1).toLowerCase()} · {formatClp(u.unit_amount_clp)}</span><br /><span className={s.muted}>Zona {u.zone}{u.activity_date ? ` · ${u.activity_date.slice(0, 10)}` : ''}</span></span>
          </label>
        ))}
      </div>
      {sel.length > 0 && <div className={s.muted}>Cubre {formatClp(covered)}; del fondo saldrán {formatClp(Math.max(total - covered, 0))}.</div>}
      {err && <Alert kind="error">{err}</Alert>}
      <div className={s.actions}>
        <Button block loading={busy} disabled={sel.length === 0} onClick={() => void apply(sel)}>Sí, usé mi viático</Button>
        <Button block variant="secondary" disabled={busy} onClick={() => void apply([])}>No, este gasto no usa viático</Button>
      </div>
      <p className={s.muted} style={{ marginTop: 4 }}>Tu revisor verá esta decisión. El viático recibido no se devuelve; solo define cuánto del gasto se descuenta del fondo.</p>
    </div>
  );
}
